import React, { useEffect, useState, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route, Switch, useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { GeneralDialog, GeneralNotificationContainer, DialogToastContainer, LoadingIndicator, ErrorBoundary } from 'sharedComponents';
import { popupTypes } from 'helpers/constants';
import useWindowSize from 'sharedHooks/useWindowSize';
import { minimumSupportedWindowWidth, portraitModeNotSupportedText } from './constants';

import { PowertrainOverview } from '../PowertrainOverview';
import { PowertrainConditionMonitoring } from '../PowertrainConditionMonitoring';
import { PowertrainTools } from '../PowertrainTools';
import { PowertrainAssetDetails } from '../PowertrainAssetDetails';
import { UserProfileSettings } from '../UserProfileSettings';
import { Login } from '../Login';
import { TokenExchange } from '../TokenExchange';
import { Logout } from '../Logout';
import { LayoutComponent } from '../Layout';
import { routes } from 'routes';
import './style.scss';
import { ReportsLazy } from '../Reports';
import featureConfigHelper from 'helpers/featureConfigHelper';
import { PowertrainFleets } from '../PowertrainFleets';
import { FleetsOverview } from '../PowertrainFleets/FleetOverview';
import useUserCapability from 'sharedHooks/useUserCapability';
import { triggerReportIssueDialog } from '../PowertrainOverview/actions';

const PortalComponent = ({ selectedOrganization }) => {
    const { t: translate } = useTranslation();
    const featureConfig = new featureConfigHelper();
    const userCapabilityHelper = useUserCapability();
    const dispatch = useDispatch();
    const [showPortraitModeNotification, setShowPortraitModeNotification] = useState(false);
    const windowSize = useWindowSize();
    const history = useHistory();
    const isREC = userCapabilityHelper.hasHomePageRecCapability(); /* FALSE FOR LSU AND END USER | TRUE FOR REC USER */
    const isLSU = userCapabilityHelper.hasFleetOverviewCapability() || userCapabilityHelper.hasHomePageCapability();
    const defaultLandingPage = isREC || isLSU ? routes.FleetDashboard : routes.ConditionMonitoring;
    const isNonPowertrainOrganization = selectedOrganization?.isPowertrainOrganization === false;

    useEffect(() => {
        history.replace(
            {
                pathname: history.location.pathname.replace('/2.0', ''),
                search: history.location.search
            });
    }, []);

    useEffect(() => {
        if (windowSize[0] && windowSize[0] < minimumSupportedWindowWidth && process.env.NODE_ENV !== 'development') {
            setShowPortraitModeNotification(true);
        } else {
            showPortraitModeNotification && setShowPortraitModeNotification(false);
        }

    }, [windowSize]);

    const handleReportIssue = () => {
        dispatch(triggerReportIssueDialog());
    };

    return <div className='portal-component'>
        <div className='notification-section'>
            <GeneralNotificationContainer />
        </div>
        <DialogToastContainer />
        <GeneralDialog
            modalClassName='minimum-res-warning-modal'
            backdropClassName='minimum-res-warning-modal-backdrop'
            show={showPortraitModeNotification}
            close={() => setShowPortraitModeNotification(false)}
            title={translate ?
                translate('ABB.Powertrain.Frontend.portalPortraitModeNotificationTitle') :
                portraitModeNotSupportedText
            }
            acceptButtonProps={{
                text: translate ? translate('ABB.Powertrain.Frontend.okLabel') : 'OK',
                onClick: () => setShowPortraitModeNotification(false)
            }}
            notificationType={popupTypes.WARNING}
            persistent={true}
        />

        <Switch>
            {/* Unauthorized Routes */}
            <Route path='/Login' component={Login} exact={true} />
            <Route path='/signin-oidc' component={TokenExchange} exact={true} />
            <Route path='/signout-callback-oidc' component={Logout} exact={true} />

            {process.env.NODE_ENV === 'development' && <Route path='/sandbox' component={require('../Sandbox').default} />}

            {/* Authorized Routes */}

            <Route>
                <PowertrainOverview>
                    {userCapabilityHelper?.isUserCapabilityLoading === false ?
                        <Suspense fallback={<LoadingIndicator />}>
                            <Switch>
                                {(isREC || isLSU) && <Route path={routes.FleetDashboard} component={PowertrainFleets} />}
                                {(isREC || isLSU) && <Route path={routes.FleetOverview} component={FleetsOverview} />}
                                <Route path={routes.ConditionMonitoring} component={PowertrainConditionMonitoring} />
                                <Route path={routes.PowertrainAndAssetDetails} component={PowertrainAssetDetails} />
                                <Route path={routes.Tools} component={PowertrainTools} />
                                {featureConfig.isReportsFeatureEnabled() &&
                                    <Route path={routes.Reports}>
                                        <ErrorBoundary onReportIssue={handleReportIssue}>
                                            <ReportsLazy />
                                        </ErrorBoundary>
                                    </Route>
                                }
                                <Route path={routes.User}>
                                    <UserProfileSettings isNonPowertrainOrganization={isNonPowertrainOrganization} />
                                </Route>
                                <Route path={'/layout'} component={LayoutComponent} />
                                <Redirect from='*' to={defaultLandingPage} />
                            </Switch>
                        </Suspense >
                        : <LoadingIndicator />}
                </PowertrainOverview>

            </Route>
        </Switch>
    </div>;
};

PortalComponent.propTypes = {
    selectedOrganization: PropTypes.shape({
        organizationID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isPowertrainOrganization: PropTypes.bool
    })
};


export default PortalComponent;
